import { LocalStorageKeys } from '@/constants/LocalStorageKeys';
import CompAddressUtils from '@/models/comp/CompAddressUtils';
import ICompAddress from '@/models/CompAddress';
import IPublicRecord from '@/models/public-records/IPublicRecord';
import PublicRecordsRequest from '@/models/requests/PublicRecordsRequest';
import WebEnvConfig from '@/models/WebEnvConfig';
import theLocalStorageUtils from '@/utils/LocalStorageUtils';
import ServiceHelperFactory from './ServiceHelperFactory';
import BaseApiService from '../BaseApiService';
import IServiceHelper from './IServiceHelper';
import IFeedData from '@/models/comp/IFeedData';
import _FakePayload from './_FakeCompPayload';
import { SourceDataInitials } from '@/constants/SourceDataInitials';
import { isFakeDataHostnameAllowed } from '@/constants/FakeDataHostnames';

// Internal use only enums and structures related to building the request.
enum PublicRecordPropertyTypes {
    Subject = 1,
    Comparable = 2,
}

interface IGetSDPRequestParams {
    address: string;
    city: string;
    state: string;
    zip: string;
    form: string;
    propertyType: PublicRecordPropertyTypes;
}

const productId = `3`; // This is QuickSource

/**
 * Service for getting public records using the QS API endpoints. Will get from either public data (v1.0/datadiscrepancy) or
 * smart data platform (v5/public-records)
 */
export default class PublicRecordsService extends BaseApiService {
    private readonly currentHostname: string;

    constructor(webEnvConfig: WebEnvConfig) {
        super(webEnvConfig.urls?.quickSourceApi ?? '');
        this.currentHostname = String(webEnvConfig.hostname || '').toLowerCase();
    }

    getServiceHelper(): IServiceHelper {
        return ServiceHelperFactory.getHelper();
    }

    /**
     * Get a feed data from the QS data discrepancy endpoint (public data) using the given request.
     * @param prRequest
     * @returns a feed data or null on not found or error
     */
    async getFromPublicData(prRequest: PublicRecordsRequest): Promise<IFeedData | null> {
        this.addContentTypeJsonHeader();
        this.addAcceptJsonHeader();

        let queryParams = {
            address: prRequest.address.address1,
            city: prRequest.address.city ?? '',
            state: prRequest.address.state ?? '',
            zip: prRequest.address.zipCode ?? '',
            isSubject: prRequest.isSubject,
            smartDataEnabled: prRequest.smartDataEnabled ?? false
        }

        const query = this.buildQuery(queryParams);
        //let response = {} as any

        try {
            const response = await this.axiosInstance.get('v7/public-records' + query);

            // This is a check to see if we should load fake data for testing purposes.  
            // Only localhost and dev environments are allowed to load fake data.
            if (
                isFakeDataHostnameAllowed(this.currentHostname) &&
                theLocalStorageUtils.hasKey('load-fake-data') &&
                theLocalStorageUtils.get('load-fake-data')
            ) {
                if(prRequest.isSubject) {
                    return _FakePayload.loadFakeData(SourceDataInitials.PublicRecords, true);
                } else {
                    return _FakePayload.loadFakeData(SourceDataInitials.PublicRecords, false);
                }
            } else {
                if (response.data) {
                    if (response.status === 200) {
                        const result: IFeedData = response.data;
                        return result;
                    }
                } else {
                    throw new Error(`PublicRecordsService.getFromPublicData(), No data returned from endpoint for address[${CompAddressUtils.getDescription(prRequest.address)}], isSubject[${prRequest.isSubject}], formCode[${prRequest.formCode}]`);
                }
            }

        } catch (error) {
            const errorDescription = this.getErrorDescription(error);
            const addressDescription = CompAddressUtils.getDescription(prRequest.address);
            console.log(`PublicRecordService.getFromPublicData(), Error[${errorDescription}] on address[${addressDescription}], isSubject[${prRequest.isSubject}], formCode[${prRequest.formCode}]`);
        }


        return null;
    }

}


